import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n";
import { langPath } from "@/lib/paths";
import { getStoredConsent, setStoredConsent } from "@/lib/consent";

const DEFAULT_CATS = {
  necessary: true,
  functional: false,
  analytics: false,
  advertising: false,
};

export default function CookieBanner() {
  const { t, language } = useI18n();
  const [visible, setVisible] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [cats, setCats] = useState(DEFAULT_CATS);

  useEffect(() => {
    const stored = getStoredConsent();
    if (!stored) {
      setVisible(true);
    } else if (stored.categories) {
      setCats({ ...DEFAULT_CATS, ...stored.categories });
    }
  }, []);

  const finish = (categories) => {
    setStoredConsent(categories);
    setCats(categories);
    setVisible(false);
    setShowModal(false);
  };

  const acceptAll = () =>
    finish({ necessary: true, functional: true, analytics: true, advertising: true });

  const rejectAll = () =>
    finish({ necessary: true, functional: false, analytics: false, advertising: false });

  if (!visible && !showModal) return null;

  return (
    <>
      {visible && !showModal && (
        <div
          className="fixed inset-x-3 bottom-3 md:inset-x-6 md:bottom-6 z-50"
          role="dialog"
          aria-live="polite"
          aria-label={t("cookies.banner.title")}
          data-testid="cookie-banner"
        >
          <div className="mx-auto max-w-4xl rounded-2xl bg-[color:var(--rv-ink)] text-[color:var(--rv-bg)] shadow-2xl p-5 md:p-6 border border-white/10">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div className="max-w-2xl">
                <div className="font-serif text-lg md:text-xl mb-2">{t("cookies.banner.title")}</div>
                <p className="text-sm opacity-90 leading-relaxed">{t("cookies.banner.text")}</p>
                <Link
                  to={langPath(language, "/cookies")}
                  className="text-xs underline underline-offset-4 opacity-80 hover:opacity-100 mt-2 inline-block"
                >
                  {t("cookies.banner.readMore")}
                </Link>
              </div>
              <div className="flex flex-col sm:flex-row md:flex-col gap-2 md:min-w-[220px]">
                <button
                  onClick={acceptAll}
                  className="rounded-full bg-[color:var(--rv-accent)] text-[color:var(--rv-ink)] px-4 py-2 text-sm font-semibold hover:bg-[color:var(--rv-accent-hover)]"
                  data-testid="cookie-accept-all"
                >
                  {t("cookies.banner.acceptAll")}
                </button>
                <button
                  onClick={rejectAll}
                  className="rounded-full border border-white/25 text-white px-4 py-2 text-sm font-medium hover:bg-white/5"
                  data-testid="cookie-reject-all"
                >
                  {t("cookies.banner.rejectAll")}
                </button>
                <button
                  onClick={() => setShowModal(true)}
                  className="rounded-full text-white/80 px-4 py-2 text-sm underline underline-offset-4 hover:text-white"
                  data-testid="cookie-manage"
                >
                  {t("cookies.banner.manage")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" role="dialog" aria-modal="true">
          <div className="w-full max-w-2xl rounded-2xl bg-white text-[color:var(--rv-ink)] shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 md:p-8">
              <div className="font-serif text-2xl mb-2">{t("cookies.modal.title")}</div>
              <p className="text-sm text-[color:var(--rv-muted)] mb-6">{t("cookies.modal.intro")}</p>

              <CategoryRow
                title={t("cookies.modal.categories.necessary.title")}
                text={t("cookies.modal.categories.necessary.text")}
                alwaysOn
                alwaysOnLabel={t("cookies.modal.categories.necessary.alwaysOn")}
                enabled
              />
              <CategoryRow
                title={t("cookies.modal.categories.functional.title")}
                text={t("cookies.modal.categories.functional.text")}
                enabled={cats.functional}
                onChange={(v) => setCats((c) => ({ ...c, functional: v }))}
              />
              <CategoryRow
                title={t("cookies.modal.categories.analytics.title")}
                text={t("cookies.modal.categories.analytics.text")}
                enabled={cats.analytics}
                onChange={(v) => setCats((c) => ({ ...c, analytics: v }))}
              />
              <CategoryRow
                title={t("cookies.modal.categories.advertising.title")}
                text={t("cookies.modal.categories.advertising.text")}
                enabled={cats.advertising}
                onChange={(v) => setCats((c) => ({ ...c, advertising: v }))}
              />

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2 mt-6">
                <button
                  onClick={() => { setShowModal(false); setVisible(true); }}
                  className="rounded-full border border-[color:var(--rv-line)] px-4 py-2 text-sm"
                >
                  {t("cookies.modal.back")}
                </button>
                <button
                  onClick={() => finish(cats)}
                  className="rounded-full bg-[color:var(--rv-ink)] text-[color:var(--rv-bg)] px-4 py-2 text-sm font-semibold"
                  data-testid="cookie-save"
                >
                  {t("cookies.modal.save")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function CategoryRow({ title, text, enabled, alwaysOn, alwaysOnLabel, onChange }) {
  return (
    <div className="flex items-start justify-between gap-4 py-4 border-b border-[color:var(--rv-line)] last:border-b-0">
      <div className="flex-1">
        <div className="font-medium">{title}</div>
        <div className="text-sm text-[color:var(--rv-muted)] mt-1">{text}</div>
      </div>
      {alwaysOn ? (
        <span className="text-xs uppercase tracking-widest text-[color:var(--rv-muted)] whitespace-nowrap">{alwaysOnLabel}</span>
      ) : (
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          onClick={() => onChange(!enabled)}
          className={`shrink-0 relative h-6 w-11 rounded-full transition-colors ${enabled ? "bg-[color:var(--rv-ink)]" : "bg-[color:var(--rv-line)]"}`}
        >
          <span
            className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${enabled ? "translate-x-5" : "translate-x-0.5"}`}
          />
        </button>
      )}
    </div>
  );
}
